import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  LogOut,
  BarChart3,
  MessageCircle,
  ArrowRight,
  Sparkles,
  Calendar,
  Zap,
  Building2,
  Mail,
  Sun,
  Moon,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Copy,
  Check,
  FileText,
  Radar,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { useContactForm } from '../context/ContactFormContext';
import { supabase } from '../supabaseClient';
import { upgradePlan, scheduleDowngrade, createCustomerPortal, cancelAtPeriodEnd, reactivateSubscription, getTransactions, getReportOrders } from '../utils/api';
import ReportIntakeModal from '../components/ReportIntakeModal';

const PLANS = [
  { id: 'pro', name: 'Pro', price: '$30', period: '/month', original: null, description: 'For serious market researchers', limitLabel: '250 queries/mo', icon: BarChart3 },
  { id: 'pro_annual', name: 'Pro Annual', price: '$25.50', period: '/month', original: null, description: '12 months of Pro billing, paid once per year.', limitLabel: '250 queries/mo', subLabel: '$306 billed annually', icon: Calendar },
  { id: 'growth', name: 'Growth', price: '$60', period: '/month', original: null, description: '4× as many queries as Pro', limitLabel: '1,000 queries/mo', icon: Zap },
  { id: 'enterprise', name: 'Enterprise', price: 'Custom', period: '', original: null, description: 'Bulk subscriptions for your company', limitLabel: 'Custom', icon: Building2, isContact: true },
];

function UserProfile() {
  const navigate = useNavigate();
  const { user, logout, refreshUserProfile, resendConfirmationEmail } = useAuth();
  const { theme, darkMode, toggleTheme } = useTheme();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  useEffect(() => {
    console.log('[UserProfile] mounted', { hasUser: !!user });
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      setTransactionsLoading(false);
      return;
    }
    let cancelled = false;
    setTransactionsLoading(true);
    setTransactionsError(null);
    getTransactions()
      .then((res) => {
        if (!cancelled && res?.transactions) setTransactions(res.transactions);
      })
      .catch((err) => {
        if (!cancelled) setTransactionsError(err.message || 'Failed to load transaction history');
      })
      .finally(() => {
        if (!cancelled) setTransactionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setReportOrdersLoading(false);
      return;
    }
    let cancelled = false;
    setReportOrdersLoading(true);
    setReportOrdersError(null);
    getReportOrders()
      .then((res) => {
        if (!cancelled && res?.report_orders) setReportOrders(res.report_orders);
      })
      .catch((err) => {
        if (!cancelled) setReportOrdersError(err.message || 'Failed to load report orders');
      })
      .finally(() => {
        if (!cancelled) setReportOrdersLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Refetch profile when visiting this page so manual DB updates (e.g. queries_limit) are visible
  useEffect(() => {
    if (user?.id) refreshUserProfile();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- only refetch when user id is set, not when refreshUserProfile identity changes

  // Load the user's enrolled TOTP factors from Supabase MFA API
  useEffect(() => {
    if (!user?.id) { setMfaStatusLoading(false); return; }
    let cancelled = false;
    supabase.auth.mfa.listFactors().then(({ data, error }) => {
      if (cancelled) return;
      setMfaFactors(!error && data ? (data.totp?.filter(f => f.status === 'verified') ?? []) : []);
    }).finally(() => { if (!cancelled) setMfaStatusLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const queriesUsed = user?.queries_used ?? 0;
  const queriesLimit = user?.queries_limit ?? 100;
  const remaining = Math.max(0, queriesLimit - queriesUsed);
  const percentUsed = queriesLimit > 0 ? Math.min(100, (queriesUsed / queriesLimit) * 100) : 0;

  const currentPlanId = useMemo(() => {
    const plan = (user?.plan || 'trial').toLowerCase();
    if (plan === 'trial') return null;
    if (plan === 'pro_annual') return 'pro_annual';
    if (plan === 'growth') return 'growth';
    if (plan === 'enterprise') return 'enterprise';
    if (plan === 'pro') return 'pro';
    return plan || null;
  }, [user?.plan]);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);   // error shown inside the cancel modal
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState(null);
  const [reportOrders, setReportOrders] = useState([]);
  const [reportOrdersLoading, setReportOrdersLoading] = useState(true);
  const [reportOrdersError, setReportOrdersError] = useState(null);
  const [reportFilesOpen, setReportFilesOpen] = useState({}); // { [orderId]: boolean }
  const [showReportIntake, setShowReportIntake] = useState(false);

  // ── 2FA / TOTP state ──────────────────────────────────────────────────────
  const [mfaFactors, setMfaFactors] = useState([]);              // verified TOTP factors
  const [mfaStatusLoading, setMfaStatusLoading] = useState(true);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAEnrollData, setTwoFAEnrollData] = useState(null); // { factorId, qrCode, secret }
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFASuccess, setTwoFASuccess] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [showDisable2FAConfirm, setShowDisable2FAConfirm] = useState(false);
  const [disable2FALoading, setDisable2FALoading] = useState(false);
  const [disable2FAError, setDisable2FAError] = useState('');
  // ─────────────────────────────────────────────────────────────────────────

  const planTier = { trial: 0, pro: 1, pro_annual: 1, growth: 2, enterprise: 3 };
  const isUpgrade = (fromId, toId) => (planTier[toId] || 0) > (planTier[fromId] || 0);
  const isDowngrade = (fromId, toId) => (planTier[toId] || 0) < (planTier[fromId] || 0);

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return;
    try {
      await logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  const handleResendVerification = async () => {
    setResendMessage(null);
    setResendLoading(true);
    try {
      const result = await resendConfirmationEmail();
      if (result.success) {
        setResendMessage('Verification email sent. Check your inbox.');
      } else {
        setResendMessage(result.error || 'Failed to resend.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setCheckoutError(null);
    setCheckoutSuccess(null);
    setCheckoutLoading(true);
    try {
      const currentId = currentPlanId || 'trial';
      if (!currentPlanId) {
        if (user?.email_confirmed === false) {
          setCheckoutError('Please verify your email first to subscribe. Check your inbox for the confirmation link.');
          setCheckoutLoading(false);
          return;
        }
        setCheckoutLoading(false);
        navigate(`/checkout?plan=${planId}`);
        return;
      }
      if (isUpgrade(currentId, planId)) {
        await upgradePlan(planId);
        setCheckoutSuccess(`Upgraded to ${PLANS.find((p) => p.id === planId)?.name || planId}. You were charged a prorated amount.`);
        await refreshUserProfile();
      } else if (isDowngrade(currentId, planId)) {
        await scheduleDowngrade(planId);
        setCheckoutSuccess(`Downgrade to ${PLANS.find((p) => p.id === planId)?.name || planId} scheduled for end of your billing period.`);
        await refreshUserProfile();
      } else {
        setCheckoutError('You are already on this plan.');
      }
    } catch (err) {
      setCheckoutError(err.message || 'Something went wrong');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelError(null);
    setCancelLoading(true);
    try {
      await cancelAtPeriodEnd();
      // Success: close modal first so the page success message is visible, then refresh profile
      setShowCancelConfirm(false);
      setCancelError(null);
      setCheckoutSuccess('Your subscription will end at the end of your current billing period. You\'ll keep access until then.');
      await refreshUserProfile();
    } catch (err) {
      // Show error INSIDE the modal so it isn't hidden behind it
      setCancelError(err.message || 'Failed to cancel. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    setCheckoutError(null);
    setReactivateLoading(true);
    try {
      await reactivateSubscription();
      setCheckoutSuccess('Your subscription has been reactivated and will renew as normal.');
      await refreshUserProfile();
    } catch (err) {
      setCheckoutError(err.message || 'Failed to reactivate subscription');
    } finally {
      setReactivateLoading(false);
    }
  };

  // ── 2FA handlers ─────────────────────────────────────────────────────────
  const handleSetup2FA = async () => {
    setTwoFAError('');
    setTwoFACode('');
    setSecretCopied(false);
    setTwoFALoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Market Scan',
      });
      if (error) throw new Error(error.message);
      setTwoFAEnrollData({
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      });
      setShow2FAModal(true);
    } catch (err) {
      setTwoFAError(err.message || 'Failed to start 2FA setup. Please try again.');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setTwoFAError('');
    setTwoFALoading(true);
    try {
      const { data: challenge, error: ce } = await supabase.auth.mfa.challenge({
        factorId: twoFAEnrollData.factorId,
      });
      if (ce) throw new Error(ce.message);
      const { error: ve } = await supabase.auth.mfa.verify({
        factorId: twoFAEnrollData.factorId,
        challengeId: challenge.id,
        code: twoFACode.trim(),
      });
      if (ve) throw new Error(ve.message);
      // Refresh factor list so the UI updates to "Enabled"
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      setMfaFactors(factorsData?.totp?.filter(f => f.status === 'verified') ?? []);
      setShow2FAModal(false);
      setTwoFAEnrollData(null);
      setTwoFACode('');
      setTwoFASuccess('Two-factor authentication has been enabled.');
      setTimeout(() => setTwoFASuccess(''), 6000);
    } catch (err) {
      setTwoFAError(err.message || 'Invalid code. Please try again.');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setDisable2FAError('');
    setDisable2FALoading(true);
    try {
      const factorId = mfaFactors[0]?.id;
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw new Error(error.message);
      setMfaFactors([]);
      setShowDisable2FAConfirm(false);
      setTwoFASuccess('Two-factor authentication has been disabled.');
      setTimeout(() => setTwoFASuccess(''), 6000);
    } catch (err) {
      setDisable2FAError(err.message || 'Failed to disable 2FA. Please try again.');
    } finally {
      setDisable2FALoading(false);
    }
  };

  const handleCopySecret = () => {
    if (twoFAEnrollData?.secret) {
      navigator.clipboard.writeText(twoFAEnrollData.secret).then(() => {
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      }).catch(() => {});
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const { openContactForm } = useContactForm();

  const formatPeriodEnd = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text }}>
      {/* Header */}
      <header style={{
        backgroundColor: theme.card,
        borderBottom: `1px solid ${theme.border}`,
        padding: '16px 0',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                <img
                  src="/logo.png"
                  alt="Market Scan"
                  style={{ width: 128, height: 128, objectFit: 'contain', borderRadius: '12px', display: 'block', backgroundColor: 'transparent' }}
                />
              </div>
            </div>

            {/* Usage, Theme Toggle & User Menu */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              {/* Theme toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                style={{
                  padding: '8px',
                  backgroundColor: theme.primaryLight,
                  color: theme.primary,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              {/* Monthly Usage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>Monthly Usage</p>
                  <div
                    style={{
                      width: '150px',
                      height: '6px',
                      backgroundColor: theme.border,
                      borderRadius: '3px',
                      marginTop: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${percentUsed}%`,
                        height: '100%',
                        backgroundColor:
                          percentUsed > 80 ? theme.usageError : percentUsed > 50 ? theme.usageWarn : theme.usageOk,
                        borderRadius: '3px',
                        transition: 'width 0.3s',
                      }}
                    />
                  </div>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: theme.textMuted }}>
                    {queriesUsed} / {queriesLimit} queries
                  </p>
                </div>
              </div>

              {/* User Menu */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  style={{
                    width: '36px',
                    height: '36px',
                    padding: 0,
                    backgroundColor: theme.primaryLight,
                    borderRadius: '50%',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  title="Profile"
                >
                  <User size={18} color={theme.primary} />
                </button>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>
                    {(user?.name || 'User').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>{user?.email}</p>
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: theme.primary,
                    backgroundColor: theme.primaryLight,
                    border: `2px solid ${theme.primary}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    boxShadow: `0 0 0 1px ${theme.primaryLight}`,
                  }}
                >
                  Profile
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: theme.primaryContrast,
                    backgroundColor: theme.primary,
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Dashboard
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '8px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: theme.textMuted,
                  }}
                  title="Log out"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Email verification banner */}
        {user && user.email_confirmed === false && (
          <section style={{
            backgroundColor: theme.primaryLight,
            border: `1px solid ${theme.primary}`,
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
            <Mail size={24} color={theme.primary} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: theme.text }}>
                Please confirm your email
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: theme.textMuted }}>
                Verify your email to unlock upgrades. Your free plan includes 5 queries per month.
              </p>
              {resendMessage && (
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: theme.usageOk }}>{resendMessage}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendLoading}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                color: theme.primary,
                backgroundColor: theme.card,
                border: `1px solid ${theme.primary}`,
                borderRadius: '8px',
                cursor: resendLoading ? 'wait' : 'pointer',
              }}
            >
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </button>
          </section>
        )}

        {/* 2FA warning banner — shown when 2FA is not set up */}
        {!mfaStatusLoading && mfaFactors.length === 0 && (
          <section style={{
            backgroundColor: theme.errorBg,
            border: `1px solid ${theme.errorBorder}`,
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
          }}>
            <ShieldAlert size={22} color={theme.errorText} style={{ flexShrink: 0, marginTop: '1px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: theme.errorText }}>
                Two-factor authentication is not enabled
              </p>
              <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: theme.errorText, opacity: 0.85 }}>
                Your account relies solely on your password. Enable 2FA with an authenticator app to add a second layer of security.
              </p>
              {twoFAError && (
                <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: theme.errorText, fontWeight: '600' }}>{twoFAError}</p>
              )}
              <button
                type="button"
                onClick={handleSetup2FA}
                disabled={twoFALoading}
                style={{
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: theme.errorText,
                  backgroundColor: 'transparent',
                  border: `1.5px solid ${theme.errorBorder}`,
                  borderRadius: '8px',
                  cursor: twoFALoading ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Shield size={14} />
                {twoFALoading ? 'Starting…' : 'Set up 2FA'}
              </button>
            </div>
          </section>
        )}

        {/* Welcome / feedback note */}
        <section style={{
          backgroundColor: theme.primaryLight,
          border: `1px solid ${theme.primaryMuted}`,
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              backgroundColor: theme.primaryMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Sparkles size={22} color={theme.primary} />
            </div>
            <div>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '700',
                color: theme.text,
              }}>
                Thank you for using Market Scan
              </h2>
              <p style={{
                margin: 0,
                fontSize: '15px',
                lineHeight: 1.6,
                color: theme.textMuted,
              }}>
                We're a small team and we want to build what helps you most. Your feedback and
                suggestions really matter. If there's something you'd like to see (a feature, a
                report, or a change), we'd love to hear from you.
              </p>
              <button
                type="button"
                onClick={() => openContactForm({ subject: 'Market Scan feedback or suggestion' })}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '16px',
                  padding: '10px 18px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.primary,
                  backgroundColor: theme.card,
                  border: `1px solid ${theme.primary}`,
                  borderRadius: '8px',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <MessageCircle size={18} />
                Send feedback or suggestions
              </button>
            </div>
          </div>
        </section>

        {/* Profile & usage card */}
        <section style={{
          backgroundColor: theme.card,
          borderRadius: '16px',
          boxShadow: theme.shadow,
          border: `1px solid ${theme.border}`,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '28px' }}>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '16px', fontWeight: '600', color: theme.text }}>Your account</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
              <div style={{
                width: '56px',
                height: '56px',
                backgroundColor: theme.primaryLight,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <User size={28} color={theme.primary} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: theme.text }}>{(user?.name || 'User').replace(/\b\w/g, (c) => c.toUpperCase())}</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: theme.textMuted }}>{user?.email}</p>
                <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: theme.textMuted }}>
                  Plan: {currentPlanId ? PLANS.find(p => p.id === currentPlanId)?.name ?? (user?.plan === 'pro' ? 'Pro' : 'Free trial') : 'Free trial'}
                </p>
              </div>
            </div>
            <div style={{ padding: '20px', backgroundColor: theme.bg, borderRadius: '12px', border: `1px solid ${theme.border}` }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: theme.text }}>Monthly usage</p>
              <div style={{ height: '10px', backgroundColor: theme.border, borderRadius: '5px', overflow: 'hidden', marginBottom: '12px' }}>
                <div style={{
                  width: `${percentUsed}%`,
                  height: '100%',
                  backgroundColor: percentUsed > 80 ? theme.usageError : percentUsed > 50 ? theme.usageWarn : theme.usageOk,
                  borderRadius: '5px',
                  transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontSize: '14px', color: theme.textMuted }}>{queriesUsed} of {queriesLimit} queries used</p>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>{remaining} remaining</p>
              </div>
            </div>
            {/* Security / 2FA row */}
            <div style={{
              marginTop: '20px',
              padding: '14px 16px',
              backgroundColor: mfaFactors.length > 0 ? theme.bg : theme.errorBg,
              borderRadius: '10px',
              border: `1px solid ${mfaFactors.length > 0 ? theme.border : theme.errorBorder}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {mfaStatusLoading ? (
                    <Shield size={16} color={theme.textMuted} />
                  ) : mfaFactors.length > 0 ? (
                    <ShieldCheck size={16} color={theme.usageOk} />
                  ) : (
                    <ShieldAlert size={16} color={theme.errorText} />
                  )}
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: mfaStatusLoading ? theme.textMuted : mfaFactors.length > 0 ? theme.text : theme.errorText,
                  }}>
                    {mfaStatusLoading
                      ? 'Checking 2FA status…'
                      : mfaFactors.length > 0
                        ? 'Two-factor authentication: Enabled'
                        : 'Two-factor authentication: Not enabled'}
                  </span>
                </div>
                {!mfaStatusLoading && (
                  mfaFactors.length > 0 ? (
                    <button
                      onClick={() => { setDisable2FAError(''); setShowDisable2FAConfirm(true); }}
                      style={{
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: theme.textMuted,
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      Disable
                    </button>
                  ) : (
                    <button
                      onClick={handleSetup2FA}
                      disabled={twoFALoading}
                      style={{
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: theme.errorText,
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.errorBorder}`,
                        borderRadius: '6px',
                        cursor: twoFALoading ? 'wait' : 'pointer',
                      }}
                    >
                      {twoFALoading ? 'Starting…' : 'Set up'}
                    </button>
                  )
                )}
              </div>
              {twoFASuccess && (
                <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: theme.usageOk, fontWeight: '500' }}>
                  ✓ {twoFASuccess}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '24px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.primaryContrast,
                  backgroundColor: theme.primary,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Open research dashboard
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => navigate('/dashboard/intel')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.textMuted,
                  backgroundColor: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <Radar size={18} />
                Competitive intel coming soon
              </button>
            </div>
            <p style={{ margin: '12px 0 0 0', fontSize: '13px', color: theme.textMuted }}>
              Competitive intel is being prepared as a separate service.
            </p>
          </div>
        </section>

        {/* Beta notice: payments disabled */}
        <div style={{
          marginBottom: '20px',
          padding: '14px 20px',
          backgroundColor: theme.primaryLight,
          border: `1px solid ${theme.primaryMuted}`,
          borderRadius: '12px',
          fontSize: '14px',
          color: theme.text,
          lineHeight: 1.5,
        }}>
          <strong>Beta build.</strong> If you think something went wrong (e.g. missing credits, something wasted your credits, or a billing issue), <button type="button" onClick={() => openContactForm({ subject: 'Market Scan feedback or suggestion' })} style={{ color: theme.primary, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>contact support</button> for extra credits or help.
        </div>

        {/* Current subscription panel */}
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: theme.text }}>Your subscription</h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            padding: '20px 24px',
            backgroundColor: currentPlanId ? theme.primaryLight : theme.bg,
            border: `2px solid ${currentPlanId ? theme.primary : theme.border}`,
            borderRadius: '16px',
            flexWrap: 'wrap',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: currentPlanId ? theme.primaryMuted : theme.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {currentPlanId ? (() => { const p = PLANS.find(x => x.id === currentPlanId); return p && <p.icon size={24} color={theme.primary} />; })() : <BarChart3 size={24} color={theme.textMuted} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: theme.text }}>
                {currentPlanId ? (PLANS.find(p => p.id === currentPlanId)?.name ?? 'Pro') : 'Free trial'}
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: theme.textMuted }}>
                {currentPlanId ? (PLANS.find(p => p.id === currentPlanId)?.description ?? '') : 'Get started with limited queries. Upgrade for more.'}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{
                padding: '6px 12px',
                backgroundColor: currentPlanId ? theme.primary : theme.textMuted,
                color: theme.primaryContrast,
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '20px',
              }}>
                Current plan
              </span>
              <span style={{ fontSize: '14px', color: theme.textMuted }}>{queriesUsed} / {queriesLimit} queries this month</span>
              {currentPlanId && user?.cancel_at_period_end && (
                <>
                  <span style={{ fontSize: '13px', color: theme.textMuted }}>
                    Cancels {user?.current_period_end ? `on ${formatPeriodEnd(user.current_period_end)}` : 'at end of billing period'} — access continues until then.
                  </span>
                  <button
                    type="button"
                    onClick={handleReactivate}
                    disabled={reactivateLoading}
                    style={{
                      padding: '8px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.primaryContrast,
                      backgroundColor: theme.primary,
                      border: 'none',
                      borderRadius: '8px',
                      cursor: reactivateLoading ? 'wait' : 'pointer',
                      opacity: reactivateLoading ? 0.8 : 1,
                    }}
                  >
                    {reactivateLoading ? 'Reactivating…' : 'Reactivate subscription'}
                  </button>
                </>
              )}
              {currentPlanId && !user?.cancel_at_period_end && (
                <button
                  type="button"
                  onClick={() => { setCancelError(null); setShowCancelConfirm(true); }}
                  disabled={cancelLoading}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#b91c1c',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: cancelLoading ? 'wait' : 'pointer',
                    opacity: cancelLoading ? 0.8 : 1,
                  }}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ── 2FA Setup Modal ────────────────────────────────────────────── */}
        {show2FAModal && twoFAEnrollData && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '24px',
            }}
            onClick={() => !twoFALoading && setShow2FAModal(false)}
          >
            <div
              style={{
                backgroundColor: theme.card,
                borderRadius: '16px',
                padding: '28px',
                maxWidth: '440px',
                width: '100%',
                boxShadow: theme.shadow,
                border: `1px solid ${theme.border}`,
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => !twoFALoading && setShow2FAModal(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: theme.textMuted,
                  cursor: twoFALoading ? 'wait' : 'pointer',
                  lineHeight: 1,
                  padding: '4px',
                }}
                aria-label="Close"
              >
                ✕
              </button>

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Shield size={20} color={theme.primary} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: theme.text }}>
                    Set Up Two-Factor Authentication
                  </p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: theme.textMuted }}>
                    Scan with Google Authenticator, Authy, or any TOTP app
                  </p>
                </div>
              </div>

              {/* QR Code */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '12px', display: 'inline-block' }}>
                  <img src={twoFAEnrollData.qrCode} alt="2FA QR code" style={{ width: 168, height: 168, display: 'block' }} />
                </div>
              </div>

              {/* Instructions */}
              <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: theme.textMuted, textAlign: 'center' }}>
                1. Open your authenticator app &nbsp;→&nbsp; 2. Scan the QR code &nbsp;→&nbsp; 3. Enter the 6-digit code below
              </p>

              {/* Manual secret */}
              <div style={{ marginBottom: '20px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: theme.textMuted, fontWeight: '500' }}>
                  Can't scan? Enter this key manually:
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: theme.bg, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                  <code style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: theme.text, letterSpacing: '0.05em', wordBreak: 'break-all' }}>
                    {twoFAEnrollData.secret}
                  </code>
                  <button
                    onClick={handleCopySecret}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: secretCopied ? theme.usageOk : theme.textMuted, padding: '2px', flexShrink: 0 }}
                    title={secretCopied ? 'Copied!' : 'Copy secret'}
                  >
                    {secretCopied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              {/* Code input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                  Enter the 6-digit verification code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '22px',
                    fontFamily: 'monospace',
                    letterSpacing: '0.25em',
                    textAlign: 'center',
                    border: `1px solid ${twoFAError ? theme.errorBorder : theme.inputBorder}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                    boxSizing: 'border-box',
                  }}
                />
                {twoFAError && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: theme.errorText }}>{twoFAError}</p>
                )}
              </div>

              {/* Verify button */}
              <button
                onClick={handleVerify2FA}
                disabled={twoFACode.length !== 6 || twoFALoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: (twoFACode.length !== 6 || twoFALoading) ? theme.primaryMuted : theme.primary,
                  color: theme.primaryContrast,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: (twoFACode.length !== 6 || twoFALoading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {twoFALoading ? 'Verifying…' : (<><ShieldCheck size={16} /> Verify & Enable 2FA</>)}
              </button>
            </div>
          </div>
        )}

        {/* ── Disable 2FA Confirm Modal ──────────────────────────────────── */}
        {showDisable2FAConfirm && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '24px',
            }}
            onClick={() => !disable2FALoading && setShowDisable2FAConfirm(false)}
          >
            <div
              style={{
                backgroundColor: theme.card,
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: theme.shadow,
                border: `1px solid ${theme.border}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <ShieldAlert size={22} color={theme.errorText} />
                <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: theme.text }}>
                  Disable two-factor authentication?
                </p>
              </div>
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', lineHeight: 1.5, color: theme.textMuted }}>
                This will remove your 2FA protection. Anyone who obtains your password would be able to access your account without additional verification.
              </p>
              {disable2FAError && (
                <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: theme.errorText }}>{disable2FAError}</p>
              )}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => !disable2FALoading && setShowDisable2FAConfirm(false)}
                  disabled={disable2FALoading}
                  style={{
                    padding: '10px 18px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme.text,
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    cursor: disable2FALoading ? 'wait' : 'pointer',
                  }}
                >
                  Keep 2FA
                </button>
                <button
                  type="button"
                  onClick={handleDisable2FA}
                  disabled={disable2FALoading}
                  style={{
                    padding: '10px 18px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#b91c1c',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: disable2FALoading ? 'wait' : 'pointer',
                    opacity: disable2FALoading ? 0.8 : 1,
                  }}
                >
                  {disable2FALoading ? 'Disabling…' : 'Disable 2FA'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel subscription confirmation modal */}
        {showCancelConfirm && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '24px',
            }}
            onClick={() => !cancelLoading && setShowCancelConfirm(false)}
          >
            <div
              style={{
                backgroundColor: theme.card,
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: theme.shadow,
                border: `1px solid ${theme.border}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: theme.text }}>
                Cancel subscription?
              </p>
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', lineHeight: 1.5, color: theme.textMuted }}>
                You'll keep access until the end of your current billing period. After that, you won't be charged again and your plan will end.
              </p>
              {cancelError && (
                <p style={{ margin: '-8px 0 16px 0', fontSize: '13px', color: theme.errorText, fontWeight: '500' }}>
                  {cancelError}
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => !cancelLoading && setShowCancelConfirm(false)}
                  disabled={cancelLoading}
                  style={{
                    padding: '10px 18px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme.text,
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    cursor: cancelLoading ? 'wait' : 'pointer',
                  }}
                >
                  Keep subscription
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  style={{
                    padding: '10px 18px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#b91c1c',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: cancelLoading ? 'wait' : 'pointer',
                    opacity: cancelLoading ? 0.8 : 1,
                  }}
                >
                  {cancelLoading ? 'Cancelling…' : 'Yes, cancel subscription'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Orders */}
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: theme.text }}>Intelligence Report Orders</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.textMuted }}>
            Your custom market intelligence reports. Files become available once your report is complete.
          </p>
          {reportOrdersError && (
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.usageError }}>{reportOrdersError}</p>
          )}
          {reportOrdersLoading ? (
            <p style={{ margin: 0, fontSize: '14px', color: theme.textMuted }}>Loading…</p>
          ) : reportOrders.length === 0 ? (
            /* Empty state — CTA card */
            <div
              style={{
                borderRadius: '12px',
                border: `1px dashed ${theme.border}`,
                backgroundColor: theme.card,
                padding: '28px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '12px',
              }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                backgroundColor: 'rgba(6,182,212,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileText size={22} color="#06b6d4" />
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '600', color: theme.text }}>
                  No reports yet
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: theme.textMuted }}>
                  Get a custom market intelligence report tailored to your business.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowReportIntake(true)}
                style={{
                  marginTop: '4px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: '#06b6d4',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Get Your First Report →
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {reportOrders.map((order, idx) => {
                const date = order.created_at
                  ? new Date(order.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                  : '—';
                const isCompleted = order.status === 'completed';
                const files = Array.isArray(order.report_files) ? order.report_files : [];
                const isOpen = !!reportFilesOpen[order.id];
                return (
                  <div
                    key={order.id}
                    style={{
                      borderRadius: '12px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.card,
                      padding: '16px 20px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>
                          Intelligence Report #{reportOrders.length - idx}
                        </span>
                        <span style={{ fontSize: '12px', color: theme.textMuted }}>{date}</span>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: isCompleted ? (theme.usageOk + '28') : 'rgba(245,158,11,0.15)',
                          color: isCompleted ? theme.usageOk : '#f59e0b',
                        }}>
                          {isCompleted ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                      {files.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setReportFilesOpen((prev) => ({ ...prev, [order.id]: !prev[order.id] }))}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 14px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: theme.primary,
                            backgroundColor: 'transparent',
                            border: `1px solid ${theme.primary}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          {isOpen ? '▲' : '▼'} Files ({files.length})
                        </button>
                      )}
                      {files.length === 0 && !isCompleted && (
                        <span style={{ fontSize: '12px', color: theme.textMuted, fontStyle: 'italic' }}>
                          Files will appear when your report is ready
                        </span>
                      )}
                    </div>

                    {/* File dropdown */}
                    {isOpen && files.length > 0 && (
                      <div style={{
                        marginTop: '14px',
                        borderTop: `1px solid ${theme.border}`,
                        paddingTop: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        {[...files].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((file) => (
                          <div
                            key={file.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 14px',
                              borderRadius: '8px',
                              backgroundColor: theme.bg,
                              border: `1px solid ${theme.border}`,
                              gap: '10px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '700',
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                                backgroundColor: file.file_type === 'html' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)',
                                color: file.file_type === 'html' ? '#3b82f6' : '#10b981',
                                flexShrink: 0,
                              }}>
                                {file.file_type || 'file'}
                              </span>
                              <span style={{ fontSize: '14px', color: theme.text, fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.label}
                              </span>
                              <span style={{ fontSize: '12px', color: theme.textMuted, flexShrink: 0 }}>
                                {new Date(file.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </div>
                            <a
                              href={file.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: theme.primaryContrast,
                                backgroundColor: theme.primary,
                                borderRadius: '7px',
                                textDecoration: 'none',
                                flexShrink: 0,
                                display: 'inline-block',
                              }}
                            >
                              Open
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Purchase another report */}
              <button
                type="button"
                onClick={() => setShowReportIntake(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#06b6d4',
                  backgroundColor: 'transparent',
                  border: `1px dashed #06b6d4`,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  marginTop: '4px',
                }}
              >
                <FileText size={16} />
                Purchase Another Report
              </button>
            </div>
          )}
        </section>

        {/* Transaction history */}
        <section style={{ marginBottom: '28px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: theme.text }}>Billing history</h3>
          {transactionsError && (
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.usageError }}>{transactionsError}</p>
          )}
          {transactionsLoading ? (
            <p style={{ margin: 0, fontSize: '14px', color: theme.textMuted }}>Loading…</p>
          ) : transactions.length === 0 ? (
            <p style={{ margin: 0, fontSize: '14px', color: theme.textMuted }}>No transactions yet.</p>
          ) : (
            <div style={{
              overflowX: 'auto',
              borderRadius: '12px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.card,
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: theme.bg, borderBottom: `2px solid ${theme.border}` }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: theme.text }}>Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: theme.text }}>Description</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: theme.text }}>Amount</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: theme.text }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const date = tx.created_at ? new Date(tx.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
                    const amount = typeof tx.amount_cents === 'number'
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: (tx.currency || 'usd').toUpperCase() }).format(tx.amount_cents / 100)
                      : '—';
                    const statusLabel = (tx.status === 'paid' && 'Paid') || (tx.status === 'payment_failed' && 'Failed') || (tx.status === 'refunded' && 'Refunded') || tx.status || '—';
                    const desc = tx.description || (tx.plan ? `${tx.plan.charAt(0).toUpperCase() + tx.plan.slice(1)} subscription` : 'Payment');
                    return (
                      <tr key={tx.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '12px 16px', color: theme.text }}>{date}</td>
                        <td style={{ padding: '12px 16px', color: theme.text }}>{desc}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: theme.text }}>{amount}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: tx.status === 'paid' ? theme.usageOk + '30' : tx.status === 'payment_failed' ? theme.usageError + '30' : theme.bg,
                            color: tx.status === 'paid' ? theme.usageOk : tx.status === 'payment_failed' ? theme.usageError : theme.textMuted,
                          }}>
                            {statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Upgrade options: horizontal panels */}
        <section>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: theme.text }}>Plans & upgrade options</h3>
          {checkoutError && (
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.usageError }}>{checkoutError}</p>
          )}
          {checkoutSuccess && (
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.usageOk }}>{checkoutSuccess}</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {PLANS.map((plan) => {
              const isCurrent = currentPlanId === plan.id;
              const Icon = plan.icon;
              return (
                <div
                  key={plan.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px 24px',
                    backgroundColor: isCurrent ? theme.primaryLight : theme.card,
                    border: `1px solid ${isCurrent ? theme.primary : theme.border}`,
                    borderRadius: '16px',
                    boxShadow: theme.shadow,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    backgroundColor: isCurrent ? theme.primaryMuted : theme.hoverBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={22} color={isCurrent ? theme.primary : theme.textMuted} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: theme.text }}>{plan.name}</p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: theme.textMuted }}>{plan.description}</p>
                    {plan.limitLabel && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.textMuted2 }}>{plan.limitLabel}</p>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'right' }}>
                      {plan.original && (
                        <span style={{ fontSize: '12px', color: theme.textMuted2, textDecoration: 'line-through', marginRight: '6px' }}>{plan.original}</span>
                      )}
                      <span style={{ fontSize: '18px', fontWeight: '700', color: theme.text }}>{plan.price}</span>
                      <span style={{ fontSize: '14px', color: theme.textMuted }}>{plan.period}</span>
                      {plan.subLabel && <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: theme.accent, fontWeight: '600' }}>{plan.subLabel}</p>}
                    </div>
                    {isCurrent ? (
                      <span style={{ padding: '6px 12px', backgroundColor: theme.primary, color: theme.primaryContrast, fontSize: '12px', fontWeight: '600', borderRadius: '20px' }}>Current</span>
                    ) : plan.isContact ? (
                      <button
                        type="button"
                        onClick={() => openContactForm({ subject: 'Enterprise subscription inquiry' })}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: theme.textMuted,
                          backgroundColor: theme.card,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <Mail size={16} />
                        Contact Sales
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={checkoutLoading}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: theme.primaryContrast,
                          backgroundColor: theme.primary,
                          border: 'none',
                          borderRadius: '8px',
                          cursor: checkoutLoading ? 'wait' : 'pointer',
                          opacity: checkoutLoading ? 0.8 : 1,
                        }}
                      >
                        {checkoutLoading
                          ? 'Processing…'
                          : isDowngrade(currentPlanId || 'trial', plan.id)
                            ? 'Downgrade'
                            : 'Upgrade'}
                        <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      <ReportIntakeModal open={showReportIntake} onClose={() => setShowReportIntake(false)} />
    </main>
    </div>
  );
}

export default UserProfile;
